import { FC, ReactNode } from 'react';
import { IonButton, IonContent, IonModal } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { IntercomService } from '../support/IntercomService';
import { DuoTriangleExclamationIcon } from '../theme/Icons';

const useStyles = createUseStyles({
    ionContent: {
        // 0px, not unitless 0: these feed Ionic's inner-scroll padding calc(), which a unitless 0 invalidates
        '--padding-top': '0px',
        '--padding-bottom': '0px',
        height: '100%',
        display: 'flex'
    },

    content: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px 32px 24px',
        textAlign: 'center'
    },

    iconContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 24
    },

    title: {
        fontSize: 18,
        fontWeight: 600,
        color: 'var(--ion-color-dark)',
        marginBottom: 16
    },

    message: {
        fontSize: 14,
        marginTop: 12,
        marginBottom: 40,
        lineHeight: 1.4,
        maxWidth: 280
    },

    button: {
        '--padding-start': '32px',
        '--padding-end': '32px',
        '--padding-top': '12px',
        '--padding-bottom': '12px',
        fontSize: 16,
        '--border-radius': '8px',
        width: '100%',
        maxWidth: 280
    },

    contactLink: {
        color: 'var(--ion-color-primary)',
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline'
        }
    }
});

interface IRecordingErrorModalProps {
    isOpen: boolean;
    title: string;
    message: string;
    actionLabel: string;
    trackingErrorType: string;
    onDismiss: () => void;
    onDidDismiss: () => void;
}

export const RecordingErrorModal: FC<IRecordingErrorModalProps> = ({
    isOpen,
    title,
    message,
    actionLabel,
    trackingErrorType,
    onDismiss,
    onDidDismiss
}) => {
    const classes = useStyles();
    const mixpanel = useInstance(MixPanelEventEmitter);
    const intercomService = useInstance(IntercomService);

    const handleDismiss = () => {
        mixpanel.track('Recording Error Acknowledged', () => ({
            'errorType': trackingErrorType,
            'title': title,
            'message': message
        }));
        onDismiss();
    };

    const handleContactUsClick = () => {
        intercomService.displayMessenger();
    };

    // Parse message to replace "contact us" with clickable link
    const renderMessage = (): ReactNode => {
        const contactUsRegex = /contact us/gi;
        const parts = message.split(contactUsRegex);

        if (parts.length === 1) {
            // No "contact us" found, return plain text
            return message;
        }

        // Build array of text and link elements
        const elements: ReactNode[] = [];
        const matches = message.match(contactUsRegex) || [];

        parts.forEach((part, index) => {
            elements.push(part);
            if (index < matches.length) {
                elements.push(
                    <span
                        key={`contact-link-${index}`}
                        className={classes.contactLink}
                        onClick={handleContactUsClick}
                    >
                        {matches[index]}
                    </span>
                );
            }
        });

        return <>{elements}</>;
    };

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onDidDismiss}
            backdropDismiss={false}
            data-id="recording-error-modal"
        >
            <IonContent className={classes.ionContent}>
                <div className={classes.content}>
                    <div className={classes.iconContainer}>
                        <DuoTriangleExclamationIcon color="secondary" customSize={80} />
                    </div>

                    <div className={classes.title}>
                        {title}
                    </div>

                    <div className={classes.message}>
                        {renderMessage()}
                    </div>

                    <IonButton
                        expand="block"
                        className={classes.button}
                        onClick={handleDismiss}
                        data-id="error-modal-action-button"
                    >
                        {actionLabel}
                    </IonButton>
                </div>
            </IonContent>
        </IonModal>
    );
};
